<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch} from "vue";
import {useStore} from "../store";
import {loadScript} from "../../../shared/load-script";
import config from "../config";
import ContinueWatchingComponent from "./ContinueWatchingComponent.vue";
import {useContentStore} from "../store/content";
import LanguageSelector from "./LanguageSelector.vue";
import {parseCookies} from "../../../lib/utils/cookies";
// import VideoAnalytics from "../../../lib/conviva";
import PlaybackReportService from "../services/reporting";
import {ErrorResponse} from "../types";
import {fetchContentPlaybackUrls} from "../services/content";

const store = useStore();
const contentItem = useContentStore();
const screen = ref(null);
const modal = ref(false);
const languageSwitcher = ref(false);

let player: window.jwplayer;
let analytics;
let tracking: PlaybackReportService;

function previewEnded(){
    modal.value = true;
    store.toggleBackdrop()
}

function handleClose(){
    modal.value = false;
    store.toggleBackdrop()
    store.previewEnded()
}

function toggleLangSwitcher(){
    languageSwitcher.value = !languageSwitcher.value;
    store.toggleBackdrop()
}

function addLanguageSwitcherBtn(){
    if (!player)
        return
    player?.addButton(
        '<svg class="jw-svg-icon" xmlns="http://www.w3.org/2000/svg"><use href="#icon-ls"/></svg>',
        "Languages",
        toggleLangSwitcher,
        "language-switcher-btn",
        "language-switcher-btn",
    )
}

function initAnalytics(player: window.jwplayer, VideoAnalytics){
    const cookies = parseCookies(document.cookie)
    const meta = {
        site: "fite",
        brand: (contentItem.brands.filter(row => row.is_primary).pop())?.name,
        genre: (contentItem.genres.filter(row => row.is_primary).pop())?.name,
        genreList: contentItem.genres.map((row) => row.name).join(","),
        cdn: "",
        policy: "",
        linear: false,
        isPaid: true,
        sid: "",
        isTrailer: false,
        preview: true
    }
    if (cookies?.sid){
        meta.sid = cookies.sid.split(":")[0]
    }
    let viewerId = "";
    if (contentItem.tags.includes("authorized") && cookies?._ga_uid){
        viewerId = cookies?._ga_uid
    }else if (cookies?.uid){
        viewerId = cookies.uid.split(":")[0]
    }
    const analytics = new VideoAnalytics(
        contentItem.itemId,
        "vod",
        true,
        config.JW_VERSION,
        viewerId,
        contentItem.tags.includes("dev"),
        meta
    )
    analytics.loadPlayer(player)
    return analytics
}

function onStopPlaybackCommand(data: ErrorResponse): void{
    const status = parseInt(data.code)
    if (status === 405){
        player.stop()
        analytics.reportPlaybackEnded()
        if(languageSwitcher.value){
            toggleLangSwitcher();
        }
        previewEnded()
    }
}

function initTracking(player: window.jwplayer){
    const reporting = new PlaybackReportService(
        contentItem.itemId,
        contentItem.reportChannelId,
        "player",
        60
    )
    reporting.onErrorResponse(onStopPlaybackCommand)
    reporting.attachPlayer(player)
    return reporting;
}

watch(() => store.itemId, (itemId) => {
    if (!player)
        return
    if (analytics)
        analytics.reportPlaybackEnded()
    if (tracking)
        tracking.remove()

    Math.round(player.getCurrentTime())
    const item = {
        sources: [{
            file: `/embed/play/${itemId}.m3u8`,
        }],
        withCredentials: true,
        starttime: Math.round(player.getCurrentTime())
    };
    player.load(item)
    analytics = initAnalytics(player);
    tracking = initTracking(player);
})

onMounted(() => {
    if(screen.value)
        (screen.value as HTMLElement).dispatchEvent(new CustomEvent(
            'switch-screen',
            {
                bubbles: true,
                detail: { visible: true}
            }))
    fetchContentPlaybackUrls(store.itemId)
        .then((data) => {
            import(/* webpackChunkName: "trl-cv" */ '../../../lib/conviva/index.js').then((module) => {


            loadScript(`/static/jwplayer-${config.JW_VERSION}/jwplayer.js`)
                .then((result) => {
                    if (!result)
                        return;

                    window.jwplayer.key = config.JW_KEY;

                    player = window.jwplayer('preview-block');
                    // eslint-disable-next-line
                    const jwparams : any = {
                        base: `/static/jwplayer-${config.JW_VERSION}/`,
                        aspectratio: '16:9',
                        width: '100%',
                        autostart: true,
                        mute: false,
                        skin: {
                            name: "seven",
                            active: "red",
                            inactive: "white",
                            background: "black"
                        },
                        pipIcon: "disabled",
                        playlist: [],
                    };

                    if (contentItem.logo){
                        jwparams.logo = contentItem.logo
                    }

                    const item = {
                        sources: [{
                            file: data.url,
                        }],
                        withCredentials: true,
                        title: contentItem.reportTitle
                    };

                    jwparams.playlist.push(item);

                    player.setup(jwparams);
                    analytics = initAnalytics(player, module.default);
                    tracking = initTracking(player)
                    if (contentItem.languages.length > 1){
                        addLanguageSwitcherBtn()
                    }
                })
            });
        })
        .catch(error => {
            console.error(error);
            store.previewEnded()
        })

})
onUnmounted(() => {
    document.querySelector<HTMLElement>('#player-holder')?.dispatchEvent(new CustomEvent(
        'switch-screen',
        {
            bubbles: true,
            detail: { visible: false}
        }))
})
</script>

<template>
    <div class="pt-2.5 md:pt-20 flex" ref="screen">
        <div class="w-full xl:px-18 flex flex-col items-center justify-center bg-black/[.67]">
            <div class="w-full xl:w-10/12">
                <div id="preview-block" class="aspect-video">

                </div>
            </div>
        </div>
        <ContinueWatchingComponent v-if="modal" @toggle="handleClose"></ContinueWatchingComponent>
        <LanguageSelector v-if="languageSwitcher" @toggle="toggleLangSwitcher"></LanguageSelector>
    </div>
</template>

<style scoped>

</style>
